import { createTheme } from '@mui/material';
import * as color from './colors';
import { bebasNeueFamily, openSansFamily } from './typography';

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      lg: 992,
      xl: 1200,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
      html {
        -webkit-font-smoothing: unset;
        -moz-osx-font-smoothing: unset;
      }
      `,
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          '&:-webkit-autofill': {
            transitionDelay: '9999s',
            transitionProperty: 'background-color, color',
          },
        },
      },
    },
  },
  palette: {
    primary: {
      light: color.neutral100,
      main: color.neutral100,
      dark: color.neutral100,
    },
    secondary: {
      light: color.neutral100,
      main: color.neutral100,
      dark: color.neutral100,
    },
    divider: color.outlineLight,
    background: {
      paper: color.backgroundLight,
      default: color.backgroundLight,
    },
    text: {
      primary: color.onSurfaceLight,
      secondary: color.neutral30,
    },
  },
  shape: {
    borderRadius: 6,
  },
});

export function mixinFontSizes(fontSizes) {
  return {
    fontSize: fontSizes.xs,
    [theme.breakpoints.up('sm')]: {
      fontSize: fontSizes.sm,
    },
    [theme.breakpoints.up('lg')]: {
      fontSize: fontSizes.lg,
    },
  };
}

theme.typography.h1 = {
  fontFamily: bebasNeueFamily,
  ...mixinFontSizes({ xs: '3.1438rem', sm: '3.5714rem', lg: '4rem' }),
  fontWeight: 300,
  letterSpacing: '0.125em',
  lineHeight: 1.1094,
  textSizeAdjust: '100%',
};

theme.typography.h3 = {
  fontFamily: openSansFamily,
  ...mixinFontSizes({ xs: '2.3572rem', sm: '2.5536rem', lg: '2.75rem' }),
  fontWeight: 600,
  letterSpacing: 'normal',
  lineHeight: 1,
  textSizeAdjust: '100%',
};

theme.typography.h4 = {
  fontFamily: openSansFamily,
  ...mixinFontSizes({ xs: '1.9125rem', sm: '2.125rem', lg: '2.125rem' }),
  fontWeight: 700,
  letterSpacing: '0.5px',
  lineHeight: 1.1765,
  textSizeAdjust: '100%',
};

theme.typography.h5 = {
  fontFamily: openSansFamily,
  ...mixinFontSizes({ xs: '1.3671rem', sm: '1.5625rem', lg: '1.5625rem' }),
  fontWeight: 400,
  letterSpacing: '0.25px',
  lineHeight: 1.28,
  textSizeAdjust: '100%',
};

theme.typography.h6 = {
  fontFamily: openSansFamily,
  ...mixinFontSizes({ xs: '1.4063rem', sm: '1.5625rem', lg: '1.5625rem' }),
  fontWeight: 700,
  letterSpacing: '-0.14px',
  lineHeight: 1.28,
  textSizeAdjust: '100%',
};

theme.typography.subtitle1 = {
  fontFamily: openSansFamily,
  ...mixinFontSizes({ xs: '1.125rem', sm: '1.25rem', lg: '1.25rem' }),
  fontWeight: 500,
  letterSpacing: '1.3px',
  lineHeight: 1.3,
  textSizeAdjust: '100%',
};

theme.typography.subtitle2 = {
  fontFamily: openSansFamily,
  fontSize: '1rem',
  fontWeight: 600,
  letterSpacing: '1.3px',
  lineHeight: 1.25,
  textSizeAdjust: '100%',
};

theme.typography.body1 = {
  fontFamily: openSansFamily,
  fontSize: '1.3125rem',
  ...mixinFontSizes({ xs: '1.1563rem', sm: '1.3125rem', lg: '1.3125rem' }),
  fontWeight: 400,
  letterSpacing: '0.25px',
  lineHeight: 1.5238,
  textSizeAdjust: '100%',
};

theme.typography.body2 = {
  fontFamily: openSansFamily,
  ...mixinFontSizes({ xs: '1.125rem', sm: '1.25rem', lg: '1.25rem' }),
  fontWeight: 400,
  letterSpacing: 'normal',
  lineHeight: 1.4,
  textSizeAdjust: '100%',
};

theme.typography.caption = {
  fontFamily: openSansFamily,
  fontSize: '1.125rem',
  fontWeight: 600,
  letterSpacing: '0.4px',
  lineHeight: 1.3333,
  textSizeAdjust: '100%',
};

theme.typography.button = {
  fontFamily: openSansFamily,
  fontWeight: 700,
  ...mixinFontSizes({ xs: '0.8750rem', sm: '0.9375rem', lg: '0.9375rem' }),
  textSizeAdjust: '100%',
};

export default theme;
